import { useState, useRef, useEffect } from "preact/hooks";
import { useRange, useProductFilters } from "@nosto/search-js/preact/hooks";
import { SearchStatsFacet } from "@nosto/nosto-js/client";
import FilterTrigger from "../FilterTrigger/FilterTrigger";
import RangeInput from "@/elements/RangeInput/RangeInput";
import { useDebounce } from "@/hooks/useDebounce";

type Props = {
  facet: SearchStatsFacet;
};

export default function RangeDropdown({ facet }: Props) {
  const { min, max, updateRange } = useRange(facet.id);
  const { filters } = useProductFilters();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasInteracted = useRef(false);

  const [localMin, setLocalMin] = useState<number | "">("");
  const [localMax, setLocalMax] = useState<number | "">("");
  const [isMinFocused, setIsMinFocused] = useState(false);
  const [isMaxFocused, setIsMaxFocused] = useState(false);

  const debouncedMin = useDebounce(localMin, 500);
  const debouncedMax = useDebounce(localMax, 500);

  const priceIsFiltered = filters.some((filter) => filter?.filter.range);

  const applyFilter = () => {
    if (localMin === "" && localMax === "" && !priceIsFiltered) return;
    if (isMinFocused && localMin === "") return;
    if (isMaxFocused && localMax === "") return;

    const finalMin = localMin === "" ? 0 : Number(localMin);
    const finalMax = localMax === "" ? max : Number(localMax);

    if (finalMax >= finalMin) {
      updateRange([finalMin, finalMax]);
    }
  };

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasInteracted.current = true;
    const val = e.currentTarget.value;
    setLocalMin(val === "" ? "" : Number(val));
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasInteracted.current = true;
    const val = e.currentTarget.value;
    setLocalMax(val === "" ? "" : Number(val));
  };

  // Reset local state when filters are cleared externally
  useEffect(() => {
    if (!priceIsFiltered) {
      setLocalMin("");
      setLocalMax("");
      hasInteracted.current = false;
    }
  }, [priceIsFiltered]);

  // Trigger API update when debounced values change
  useEffect(() => {
    if (hasInteracted.current && isOpen) {
      applyFilter();
    }
  }, [debouncedMin, debouncedMax]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="filter-facets__nav-item"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <FilterTrigger
        value={facet.name}
        isOpen={isOpen}
        onKeyDown={handleKeyDown}
        ariaLabel={`Filter by ${facet.name}`}
      />

      {isOpen && (
        <div className="filter-facets__nav-item-options" role="menu">
          <div className="price-range-filter" role="menuitem">
            <div className="price-range-filter__input br-input">
              <label htmlFor="desktop-min-price" className="br-input__label">
                Min
              </label>
              <RangeInput
                id="desktop-min-price"
                value={localMin}
                min={min}
                max={max}
                placeholder="0"
                onChange={handleMinChange}
                onFocus={() => {
                  setIsMinFocused(true);
                  setLocalMin("");
                }}
                onBlur={() => setIsMinFocused(false)}
                className="br-input__control br-input__control--text"
              />
            </div>
            <div className="price-range-filter__input br-input">
              <label htmlFor="desktop-max-price" className="br-input__label">
                Max
              </label>
              <RangeInput
                id="desktop-max-price"
                value={localMax}
                min={min}
                max={max}
                onChange={handleMaxChange}
                onFocus={() => {
                  setIsMaxFocused(true);
                  setLocalMax("");
                }}
                onBlur={() => setIsMaxFocused(false)}
                className="br-input__control br-input__control--text"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
